/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { compact, snakeCase, trim } from 'lodash';
import { Account } from '../../../account-data/account.model';
import {
  DepositTypesEnum,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { States } from '../../../document-data/sub-models/states.model';
import { EstimateStatusesEnum } from '../../../estimate-data/estimate-data.data';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { LocalizationService } from '../../../localization/localization.service';
import { CompanyInfo } from '../../../settings-data/sub-models/company-info.model';
import { EmailDefault } from '../../../settings-data/sub-models/company-settings/email-default.model';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { PercentagePipe } from '../../../shared/pipes/percentage/percentage.pipe';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DateTimeService } from 'i2g-ng-utils';

export interface IDocumentMessageData {
  buttonCta: string;
  subject: string;
  attachmentName: string;
  paymentNotificationsText: SafeHtml;
  message: string;
  cc: string;
  bcc: string;
}

@Injectable()
export class ContentSendBuilder {
  constructor(
    private _localizationService: LocalizationService,
    private _domSanitizer: DomSanitizer,
    private _dateTimeService: DateTimeService,
    private _percentagePipe: PercentagePipe,
    private _moneyPipe: MoneyPipe,
    private _friendlyDatePipe: FriendlyDatePipe
  ) {}

  buildMessageData(
    translations: any,
    documentType: DocumentTypesEnum,
    account: Account,
    documentState: States,
    companyInfo: CompanyInfo,
    documentNumber: string,
    emailDefault: EmailDefault,
    documentCalculationResult: CalculationResult,
    documentDueDate: string,
    deposit: Deposit,
    documentLocale: string,
    hasBankTransfer: boolean,
    hasCardPayments: boolean,
    hasPaypalEc: boolean,
    documentSettings: DocumentPresetSettings,
    paymentsSettings: PaymentsSettings,
    featureToggles: Toggles
  ): IDocumentMessageData {
    const documentTypeKey = `DOCUMENT.TYPE.${documentType}`.toUpperCase();
    const documentCurrencyCode = documentSettings.currencyCode;
    const translatedType = this._localizationService.getParsedResult(
      translations,
      documentTypeKey
    );
    const filename = `${snakeCase(translatedType)}_${documentNumber}`.replace(
      /[^A-Za-z0-9-_]/g,
      ''
    );
    const subject: string = this._buildSubject(
      translations,
      companyInfo,
      documentNumber,
      account,
      documentType
    );
    const attachmentName = `${filename.toLocaleLowerCase()}.pdf`;
    const viewTextKey = `DOCUMENT.${documentType}.EMAIL.VIEW_TEXT`.toUpperCase();
    const buttonCta: string = this._localizationService.getParsedResult(
      translations,
      viewTextKey
    );
    let paymentNotificationsText: SafeHtml;
    let message: string = emailDefault.message || '';

    switch (documentType) {
      case DocumentTypesEnum.invoice:
        if (
          featureToggles.isAllAccess(Toggles.KEYS.EMAIL_TEMPLATE_EXPERIMENT)
        ) {
          break;
        }

        message += this._buildDepositMessage(
          translations,
          documentType,
          documentCalculationResult,
          deposit,
          documentCurrencyCode,
          documentLocale
        );

        message += this._buildInvoicePaymentMessage(
          translations,
          documentCalculationResult,
          hasBankTransfer,
          hasCardPayments,
          hasPaypalEc
        );

        paymentNotificationsText = this._domSanitizer.bypassSecurityTrustHtml(
          this._localizationService.getParsedResult(
            translations,
            'EMAIL.PAYMENTS_NOTIFICATION'
          )
        );

        break;
      case DocumentTypesEnum.estimate:
        if (featureToggles.isAllAccess(Toggles.KEYS.ESTIMATE_APPROVAL)) {
          message += this._buildApprovalMessage(
            translations,
            documentType,
            documentState
          );
        }

        const depositMessage = this._buildDepositMessage(
          translations,
          documentType,
          documentCalculationResult,
          deposit,
          documentCurrencyCode,
          documentLocale
        );
        const joiner = `\n\n${this._localizationService.getParsedResult(
          translations,
          'LABEL.OR'
        )}`.toUpperCase();

        message += compact([depositMessage]).join(joiner);

        if (
          featureToggles.isAllAccess(Toggles.KEYS.ESTIMATE_PAYABLE) &&
          deposit
        ) {
          message += this._buildPaymentMessage(
            translations,
            documentCalculationResult,
            hasBankTransfer,
            hasCardPayments,
            hasPaypalEc
          );
        }

        break;
      case DocumentTypesEnum.credit_memo:
      // fall through
      case DocumentTypesEnum.purchase_order:
      // fall through
      default:
        break;
    }

    return {
      buttonCta,
      subject,
      attachmentName,
      paymentNotificationsText,
      message: trim(message),
      bcc: emailDefault.bcc || '',
      cc: emailDefault.cc || '',
    };
  }

  private _buildSubject(
    translations: any,
    companyInfo: CompanyInfo,
    documentNumber: string,
    account: Account,
    documentType: DocumentTypesEnum
  ) {
    const subjectTranslateParams = {
      companyName: companyInfo.name || account.email,
      documentNumber,
    };
    const subjectKey: string = `DOCUMENT.${documentType}.EMAIL.SUBJECT`.toUpperCase();

    return this._localizationService.getParsedResult(
      translations,
      subjectKey,
      subjectTranslateParams
    );
  }

  private _buildDepositMessage(
    translations: any,
    documentType: DocumentTypesEnum,
    documentCalculationResult: CalculationResult,
    deposit: Deposit,
    documentCurrencyCode: string,
    documentLocale: string
  ): string {
    if (
      documentCalculationResult.payments.deposit &&
      documentCalculationResult.payments.deposit.isUnpaid()
    ) {
      const today = this._dateTimeService.formatDate();
      const numOfDaysAhead = this._dateTimeService.calculateNumberOfUnitsFromDate(
        deposit.dueDate,
        today
      );

      if (documentType === DocumentTypesEnum.invoice) {
        let depositRequestTranslateKey: string;

        if (numOfDaysAhead >= 7) {
          // due in more than 7 days
          depositRequestTranslateKey = 'DEPOSIT_REQUEST_DUE_BY_DATE';
        } else if (numOfDaysAhead < 0) {
          // overdue
          depositRequestTranslateKey = 'DEPOSIT_REQUEST_OVERDUE';
        } else if (numOfDaysAhead === 0) {
          // due today
          depositRequestTranslateKey = 'DEPOSIT_REQUEST_DUE_TODAY';
        } else if (numOfDaysAhead === 1) {
          // due tomorrow
          depositRequestTranslateKey = 'DEPOSIT_REQUEST_DUE_IN_1_DAY';
        } else if (numOfDaysAhead <= 7) {
          // due within 7 days
          depositRequestTranslateKey = 'DEPOSIT_REQUEST_DUE_WITHIN_7_DAYS';
        }

        const depositRequestTranslateParams = {
          amount: this._moneyPipe.transform(
            documentCalculationResult.payments.deposit.amountRemaining,
            documentCurrencyCode,
            documentLocale
          ),
          dueDate: this._friendlyDatePipe.transform(deposit.dueDate),
          numOfDays: numOfDaysAhead,
        };
        const depositRequestMessage = this._localizationService.getParsedResult(
          translations,
          `DOCUMENT.INVOICE.EMAIL.${depositRequestTranslateKey}`,
          depositRequestTranslateParams
        );

        return `\n\n${depositRequestMessage}`;
      } else if (documentType === DocumentTypesEnum.estimate) {
        let depositRequestTranslateKey: string;

        if (deposit.type === DepositTypesEnum.fixed_amount) {
          if (numOfDaysAhead === 0) {
            // due today
            depositRequestTranslateKey = 'DEPOSIT_REQUEST_FIXED_TODAY';
          } else {
            depositRequestTranslateKey = 'DEPOSIT_REQUEST_FIXED_BY_DATE';
          }
        } else {
          if (numOfDaysAhead === 0) {
            // due today
            depositRequestTranslateKey = 'DEPOSIT_REQUEST_PERCENTAGE_TODAY';
          } else {
            depositRequestTranslateKey = 'DEPOSIT_REQUEST_PERCENTAGE_BY_DATE';
          }
        }

        const depositRequestTranslateParams = {
          amount: this._moneyPipe.transform(
            documentCalculationResult.payments.deposit.amountRemaining,
            documentCurrencyCode,
            documentLocale
          ),
          percentage: this._percentagePipe.transform(
            deposit.percentage,
            documentLocale,
            0
          ),
          date: this._friendlyDatePipe.transform(deposit.dueDate),
        };
        const depositRequestMessage = this._localizationService.getParsedResult(
          translations,
          `DOCUMENT.ESTIMATE.EMAIL.${depositRequestTranslateKey}`,
          depositRequestTranslateParams
        );

        return `\n\n${depositRequestMessage}`;
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  private _buildPaymentMessage(
    translations: any,
    documentCalculationResult: CalculationResult,
    hasBankTransfer: boolean,
    hasCardPayments: boolean,
    hasPaypalEc: boolean
  ): string {
    if (documentCalculationResult.outstandingBalance > 0) {
      let messageKey = '';

      if (hasBankTransfer && hasCardPayments && hasPaypalEc) {
        messageKey = 'CARDS_PAYPAL_BANK_TRANSFER';
      } else if (hasBankTransfer && hasCardPayments) {
        messageKey = 'CARDS_AND_BANK_TRANSFER';
      } else if (hasCardPayments && hasPaypalEc) {
        messageKey = 'CARDS_AND_PAYPAL';
      } else if (hasBankTransfer && hasPaypalEc) {
        messageKey = 'PAYPAL_AND_BANK_TRANSFER';
      } else if (hasBankTransfer) {
        messageKey = 'BANK_TRANSFER_ONLY';
      } else if (hasCardPayments) {
        messageKey = 'CARDS_ONLY';
      } else if (hasPaypalEc) {
        messageKey = 'PAYPAL_ONLY';
      }

      if (messageKey) {
        messageKey = `EMAIL.PAYMENTS_INFO.${messageKey}`;

        return `\n\n${this._localizationService.getParsedResult(
          translations,
          messageKey
        )}`;
      }
    }

    return '';
  }

  private _buildInvoicePaymentMessage(
    translations: any,
    documentCalculationResult: CalculationResult,
    hasBankTransfer: boolean,
    hasCardPayments: boolean,
    hasPaypalEc: boolean
  ): string {
    return documentCalculationResult.outstandingBalance > 0 &&
      (hasBankTransfer || hasCardPayments || hasPaypalEc)
      ? `\n\n${this._localizationService.getParsedResult(
          translations,
          'DOCUMENT.INVOICE.EMAIL.ONLINE_PAYMENTS_TEXT'
        )}`
      : '';
  }

  private _buildApprovalMessage(
    translations: any,
    documentType: DocumentTypesEnum,
    documentState: States
  ): string {
    return documentType === DocumentTypesEnum.estimate &&
      documentState.estimateStatus === EstimateStatusesEnum.pending
      ? `\n\n${this._localizationService.getParsedResult(
          translations,
          'DOCUMENT.ESTIMATE.EMAIL.APPROVAL_TEXT'
        )}`
      : '';
  }
}
